import { useEffect } from "react";

import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import { saveAs } from "file-saver";

import { useApplyFilters } from "./useApplyFilters";

import { fetchExportPreventiveSlice } from "../slices/exportPreventiveSlice";

import { setShowAlert } from "../slices/alertSlice";
import ExcelJS from "exceljs";

export const usePreventiveExport = ({ componentId }) => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.auth.token);
    const meta = useSelector((state) => state.exportPreventive.meta);
    const columns = useSelector((state) => state.exportPreventive.columns);

    const { params } = useApplyFilters({componentId});

    useEffect(() => {
        if (!meta || Object.keys(meta).length === 0) {
            dispatch(fetchExportPreventiveSlice({token, params, page: 1}));
        }
    }, [dispatch, token, params, meta]);

    const handleExport = async () => {
        if (!meta || !meta.last_page) {
            dispatch(setShowAlert({
                type: "failure",
                messages: "Ошибка, Meta не инициализирована",
                context: "exportData"
            }));
            return;
        }

        const total_pages = meta.last_page;
        let currentPage = 1;
        const allData = [];

        try {
            while (currentPage <= total_pages) {
                const result = await dispatch(fetchExportPreventiveSlice({ token, params, page: currentPage })).unwrap();
                if (!result || !result.data) {
                    console.error("Ошибка: данные экспорта отсутствуют", result);
                    return;
                }
                allData.push(...result.data);
                currentPage++;
            }

            if (!columns || !Array.isArray(columns)) {
                console.error("Ошибка: columnsExport не является массивом", columns);
                return;
            }

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Export");

            const originalWidths = [
                13.0, 18.3, 18.4, 19.2, 19.3,
                22.2, 22.3
            ];
            const defaultWidth = 30;

            worksheet.columns = columns.map((col, index) => ({
                header: col.alias || col.name,
                key: col.key,
                width: originalWidths[index] ?? defaultWidth,
                style: {
                    alignment: {
                        wrapText: true,
                        vertical: "top"
                    }
                }
            }));

            worksheet.getRow(1).eachCell((cell) => {
                cell.font = { bold: true };
                cell.alignment = { wrapText: true, vertical: "top" };
            });

            allData.forEach((item) => {
                const attributes = Array.isArray(item.attributes) ? item.attributes[0] : item.attributes;
                const row = {};
                columns.forEach((col) => {
                    const key = col.key;
                    let value = attributes[key];

                    if (Array.isArray(value)) {
                        row[key] = value.length > 0 ? value.join(", ") : "Отсутствует";
                    } else {
                        row[key] = value !== null && value !== undefined ? value : "Отсутствует";
                    }
                });
                worksheet.addRow(row);
            });

            worksheet.eachRow((row) => {
                let maxLines = 1;

                row.eachCell((cell, colNumber) => {
                    const text = cell.value?.toString() || "";
                    const colWidth = worksheet.getColumn(colNumber).width || defaultWidth;

                    const estimatedLines = Math.ceil(text.length / (colWidth * 1.2));
                    maxLines = Math.max(maxLines, estimatedLines);

                    cell.alignment = { wrapText: true, vertical: "top" };

                    cell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" }
                    };
                });
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            saveAs(blob, "Preventive.xlsx");

        } catch (error) {
            dispatch(setShowAlert({
                type: "failure",
                messages: error.message,
                context: "exportData"
            }));
        }
    };

    return { handleExport }
}